import { SearchRegistry } from '../types';

export const rydMarketingSearchEntries: SearchRegistry = {
  '/ryd-marketing/overview': {
    keywords: [
      'ryd marketing',
      'marketing overview',
      'marketing dashboard',
      'marketing tools',
      'marketing landing',
      'marketing module list',
      'marketing modules',
    ],
    questions: [
      'Where is the ryd Marketing overview?',
      'How do I open the marketing tools section?',
    ],
  },
  '/ryd-marketing/deeplinking/add-dynamic-parameters': {
    keywords: [
      'deeplink',
      'dynamic parameters',
      'deeplink parameters',
      'utm',
      'tracking parameters',
      'campaign parameters',
      'app links',
      'adjust deeplink',
      'adjust',
      'go.link',
      'ryd.go.link',
      'adj_t',
      'adj_fallback',
      'adj_redirect_macos',
      'voucher_code',
      'user_id parameter',
      'query string format',
      'url encoded',
      'copy to clipboard',
    ],
    questions: [
      'How do I add dynamic parameters to a marketing deeplink?',
      'How do I attach UTM tracking to a deeplink?',
      'What format should I use for dynamic parameters?',
      'How does the tool encode parameters into an Adjust deeplink?',
    ],
    faqs: [
      {
        q: 'How do I add tracking parameters to a marketing deeplink?',
        a: 'Open ryd Marketing → Deeplinking → Add Dynamic Parameters. Paste the full Adjust deeplink (not the short click URL) into Original Adjust Deeplink, type the parameters in query-string format (e.g. `voucher_code=12345&user_id=abc123`) and click Generate. The tool URL-encodes the values and inserts them into the right places in the deeplink.',
      },
    ],
  },
  '/voucher-campaigns/statistics': {
    keywords: [
      'discount campaign',
      'voucher campaign',
      'campaign statistics',
      'landing page statistics',
      'landing page visits',
      'promo statistics',
      'stellantis campaign',
      'co-branded voucher',
      'campaign conversion',
      'campaign traffic',
      'promo landing page',
    ],
    questions: [
      'How many people visited the Stellantis campaign landing page?',
      'What is the conversion rate of the campaign landing page?',
      'Where do I see the campaign links we gave the partner?',
    ],
    faqs: [
      {
        q: 'How many users visited the campaign landing page?',
        a: 'Open Discount Campaigns → Landing Page Statistics. Visits are counted server-side on every page load of /promo/<slug>, so the figures are exact. Google Analytics runs on those pages in cookieless consent-denied mode and reports modelled numbers only, which is why the portal keeps its own counter.',
      },
    ],
  },
  '/voucher-campaigns/order-statistics': {
    keywords: [
      'discount campaign',
      'voucher campaign',
      'order statistics',
      'campaign orders',
      'voucher consumption',
      'voucher redemption',
      'discount used',
      'stellantis campaign',
      'approved customers orders',
    ],
    questions: [
      'How many orders did the approved campaign customers place?',
      'Which discounts did the Stellantis campaign customers use?',
      'Which approved customers have not ordered yet?',
    ],
    faqs: [
      {
        q: 'Where do I see whether campaign customers used their discount?',
        a: 'Open Discount Campaigns → Order Statistics. It lists the orders of every approved customer since the campaign started and the discounts those orders carried, by name. The figures come from the ryd data warehouse, so they follow the live systems with a delay. Open a customer to see each order and the discount it used.',
      },
    ],
  },
  '/voucher-campaigns/how-it-works': {
    keywords: [
      'discount campaign',
      'voucher campaign',
      'how it works',
      'campaign process',
      'campaign documentation',
      'eligibility decision',
      'sign-up check',
      'approve before sign-up',
      'not eligible',
      'campaign emails',
      'campaign states',
      'flow diagram',
    ],
    questions: [
      'How does the Stellantis discount campaign work end to end?',
      'What happens after a customer registers on the landing page?',
      'What does ryd do automatically and what do I have to do?',
      'Which emails does a campaign customer receive, and when?',
    ],
    faqs: [
      {
        q: 'Where is the whole discount campaign process explained?',
        a: 'Discount Campaigns → How It Works. One animated diagram for each of the three cases — the customer already has a ryd account, you decide before they sign up, and you mark someone not eligible — plus the three emails and what every state means.',
      },
      {
        q: 'What does the sign-up check do?',
        a: 'Every 15 minutes ryd looks for new ryd accounts behind waiting addresses, adds the campaign to the wallet of anyone who now has one, carries out approvals made earlier, and makes sure anyone marked not eligible does not end up with the campaign in their wallet. Nothing on the operator pages needs you to trigger it.',
      },
    ],
  },
  '/voucher-campaigns/registrations': {
    keywords: [
      'campaign registrations',
      'discount registrations',
      'voucher registrations',
      'approve voucher',
      'approve customer',
      'stellantis tag',
      'campaign tag',
      'activate voucher',
      'voucher consumption',
      'email history',
      'registered in ryd',
      'promo registrations',
      'eligibility decision',
      'approve before sign-up',
      'not eligible',
      'sign-up check',
    ],
    questions: [
      'How do I approve a customer for the Stellantis discount campaign?',
      'Does this email address have a ryd account?',
      'Can I decide before the customer has signed up with ryd?',
      'Which campaign emails were sent to a customer?',
      'Has the customer used their discount?',
    ],
    faqs: [
      {
        q: 'How do I approve a customer for a discount campaign?',
        a: 'Open Discount Campaigns → Campaign Registrations, open the customer and press "Approve and activate discount". That adds the campaign to their ryd wallet, activates the discount and sends the activation email.',
      },
      {
        q: 'Can I decide before the customer has signed up with ryd?',
        a: 'Yes. The decision never waits on the sign-up. Approving an address with no ryd account stores it as "Approved — waiting for sign-up"; the sign-up check runs every 15 minutes, and the moment an account appears the discount is added and the activation email sent, crediting the reviewer who approved it.',
      },
      {
        q: 'What happens to someone who registered but has no ryd account?',
        a: 'The registration is stored as "Waiting for ryd sign-up" and the sign-up check re-asks the ryd system every 15 minutes. As soon as an account appears, the campaign is added to their ryd wallet and the row becomes "Ready for review" — or, if you already decided, your decision is carried out straight away.',
      },
      {
        q: 'What does marking someone not eligible actually do?',
        a: 'It records your reason, emails the customer and removes the campaign from their ryd wallet so it no longer shows in the app. If they have not signed up yet, the campaign is kept out of their wallet when they do. The decision can be reversed later by approving them, which needs its own reason.',
      },
    ],
  },
  '/ryd-marketing/cdn-management/cdn-file-management': {
    keywords: [
      'cdn',
      'cdn management',
      'cdn file management',
      'asset upload',
      'static assets',
      'images',
      'campaign assets',
      'cdn upload',
      'ryd cdn',
      'file browser',
      'upload files',
      'create folder',
      'new folder',
      'folder navigation',
      'file size limits',
      'naming conventions',
      'supported formats',
      'cdn folders',
    ],
    questions: [
      'How do I upload an asset to the CDN?',
      'Where do I see and manage CDN files?',
      'Which file formats and sizes does the CDN support?',
      'How do I create a new folder in the CDN?',
    ],
    faqs: [
      {
        q: 'How do I upload an asset to the ryd CDN?',
        a: 'Open ryd Marketing → CDN Management → CDN File Management. First navigate to the target folder in the File Browser, then click Upload Files (or use New Folder to create a subfolder first). Follow the naming conventions and size limits documented in the Important Notes section on the same page.',
      },
    ],
  },
  '/ryd-marketing/mandrill-templates/template-browser': {
    keywords: [
      'mandrill',
      'mandrill templates',
      'email templates',
      'template browser',
      'mailchimp',
      'transactional emails',
      'email preview',
      'mandrill transactional templates',
      'subject search',
      'slug search',
      'template name search',
      'email body search',
      'fetch email body',
      'bigquery email body',
      'template label',
      'min characters',
      'template metadata',
    ],
    questions: [
      'How do I browse Mandrill email templates?',
      'Where do I find a specific transactional email template?',
      'How do I search inside the email body of templates?',
      'How do I refresh the stored body of a Mandrill template?',
    ],
    faqs: [
      {
        q: 'Can I search inside the body of a Mandrill template, not just by subject?',
        a: 'Yes — the Mandrill Templates page has two search inputs. The first searches metadata (subject / slug / name). The second searches the stored HTML/text body via BigQuery (minimum 3 characters). Use Fetch Email Body on individual rows to refresh the stored content if it looks out of date.',
      },
    ],
  },
  '/ryd-marketing/mandrill-templates/template-usage-analytics': {
    keywords: [
      'template usage',
      'mandrill analytics',
      'email analytics',
      'template stats',
      'email usage',
      'template metrics',
      'engagement help modal',
      'send count',
      'open rate',
      'click rate',
      'template charts',
    ],
    questions: [
      'Where do I see the usage analytics of Mandrill templates?',
      'Which email templates are sent most often?',
      'How are open and click rates computed?',
    ],
  },
  '/ryd-marketing/mailchimp-audience-export': {
    keywords: [
      'mailchimp',
      'mailchimp audience export',
      'audience export',
      'mailchimp import',
      'manager export',
      'driver export',
      'nfc order sync',
      'export records',
      'approve export records',
      'reject export records',
      'marketing automation',
      'merge fields',
      'data quality warnings',
      'dry run import',
      'mailchimp scheduler',
    ],
    questions: [
      'How do I export fleet managers or drivers to Mailchimp?',
      'Where do I review and approve Mailchimp export records?',
      'How do I run a Mailchimp import?',
      'How do I activate the Mailchimp export scheduler?',
    ],
    faqs: [
      {
        q: 'How does the Mailchimp audience export work?',
        a: 'Open ryd Marketing -> Marketing Automation -> Mailchimp Audience Export. Scheduled or manually triggered runs sync NFC orders and prepare one export record per manager or driver with merge fields and data-quality warnings. Review the records, approve or reject them, and then start the manual import per audience, optionally as a dry run first.',
      },
      {
        q: 'Does anything get sent to Mailchimp automatically?',
        a: 'No. Automatic Mailchimp sending is disabled. The manual import on this page is the only sending path. Scheduled runs only prepare records for review.',
      },
      {
        q: 'Which records does the import send?',
        a: 'Only approved records of the selected audience are claimable by an import run. Rejected or still-prepared records are never sent.',
      },
      {
        q: 'How do I add a fleet to the Mailchimp export target companies?',
        a: 'Open Fleet Operations -> Partner List, click Marketing Data on the fleet partner, and set Include In Mailchimp Audience Export to Enabled. Flagged partners replace the static rollout list; disabling stops new export records but does not archive existing Mailchimp records.',
      },
    ],
  },
  '/ryd-marketing/mailchimp-audience-export/knowledge-base': {
    keywords: [
      'mailchimp export knowledge base',
      'mailchimp field mapping',
      'mailchimp merge fields',
      'ttpenabled',
      'ttpenabldt',
      'ttpenabld',
      'nfc payment enabled date',
      'mailchimp source fetches',
      'driver export fields',
      'manager export fields',
    ],
    questions: [
      'How is TTPENABLD determined?',
      'How is TTPENABLDT determined?',
      'Where do Mailchimp export fields come from?',
      'How does the Mailchimp audience export fetch source data?',
      'Do repeated Mailchimp export runs create duplicate records?',
    ],
    faqs: [
      {
        q: 'Do repeated Mailchimp export runs create duplicate records?',
        a: 'No. Every person has a stable record key (audience + sponsor + email hash), so runs upsert: new people are inserted as Prepared, changed people are updated and reset to Prepared for re-review, unchanged people keep their status, returning people are reactivated, and people missing from the source are archived. Details in the Record Lifecycle section of the Mailchimp Export Knowledge Base.',
      },
      {
        q: 'How is TTPENABLDT determined?',
        a: 'Open ryd Marketing -> Marketing Automation -> Mailchimp Export KB. A manually maintained Tap To Pay activation date (Marketing Data dialog on Fleet Operations -> Partner List) always wins. Otherwise TTPENABLDT comes from stored sponsor feature state or the latest nfcPayment activation audit event, with a first-observed fallback and warning when no audit transition exists.',
      },
    ],
  },
  '/ryd-marketing/pitch-builder/knowledge-base': {
    keywords: [
      'pitch builder',
      'pitch builder guide',
      'pitch builder knowledge base',
      'claude artifact',
      'artifact contract',
      'import pitch builder',
      'update pitch builder',
      'new pitch builder version',
      'pitch builder dry run',
      'pitch builder sensitive gate',
      'pitch builder metadata',
      'saved pitches',
      'export pitch deck',
      'publish pitch deck',
    ],
    questions: [
      'How do I use the Pitch Builder?',
      'How do I save or export a pitch deck?',
      'How do I update the Pitch Builder after Marketing publishes a new Claude version?',
      'What command imports a new Pitch Builder artifact?',
      'What is the Pitch Builder sensitivity gate?',
      'When should I update Pitch Builder Metadata instead of importing a new artifact?',
    ],
    faqs: [
      {
        q: 'How do I import a new Pitch Builder version from Claude?',
        a: 'Open ryd Marketing -> Pitch Builder -> Knowledge Base. Export the Claude artifact folder, run `npm run import:pitch-builder -- "/path/to/export folder" --dry-run`, then run `npm run import:pitch-builder -- "/path/to/export folder"` after validation passes. Verify the new Artifact vN in the Pitch Builder header.',
      },
      {
        q: 'When do I use Pitch Builder Metadata instead of importing a new artifact?',
        a: 'Use Pitch Builder Metadata for central facts such as company stats, product copy, comparison rows, savings parameters, and rep defaults. Import a new Claude artifact only when the builder UI, slide logic, or bundled assets changed.',
      },
    ],
  },
  '/ryd-marketing/homepage/partner-affiliate-links': {
    keywords: [
      'partner affiliate',
      'affiliate links',
      'homepage affiliates',
      'partner links',
      'affiliate program',
      'referral links',
      'partner links page',
      'affiliate management',
      'affiliate tracking',
      'partner offers',
    ],
    questions: [
      'Where do I manage homepage partner affiliate links?',
      'How do I add a new affiliate link to the marketing homepage?',
      'How are affiliate clicks tracked?',
    ],
  },
  '/ryd-marketing/homepage/awin-pushes': {
    keywords: [
      'awin',
      'awin pushes',
      'affiliate network',
      'awin integration',
      'affiliate pushes',
      'partner pushes',
      'awin push notifications',
      'awin notification',
      'affiliate notification',
    ],
    questions: [
      'What are Awin pushes?',
      'How do I trigger or manage an Awin affiliate push?',
      'Where do I see the history of Awin push notifications?',
    ],
  },
};
